import { useContext, useState } from "react";
import ExcelCopy, { ExcelCopyContext, ExcelCopyProvider, ParsedRows } from "./ExcelCopy";
import _ from "lodash";
import { createStandardizedPatentNumber, deduceIpType, standardizeCountryCode } from "./standardize";
import { epoLoad, EpoScrapeMember } from "src/backend";
import { TableCopyButton } from "src/components/button/TableCopyButton";

export function ExcelListImport() {
  return <ExcelCopyProvider>
      <ExcelListImportUi />
  </ExcelCopyProvider>
}

function ExcelListImportUi() {
  const {rows, withHeader, setWithHeader, epoData} = useContext(ExcelCopyContext);
  return (
    <>
      <div className="p-4 max-w-2xl">
        <div className="flex flex-row gap-8">
          <label className="label">
            <input type="checkbox" className="form-checkbox" checked={withHeader} onChange={e => setWithHeader(e.target.checked)} /> With
            Header{' '}
          </label>
          {epoData.length > 0 && (
            <TableCopyButton tableId="parsed-rows" className="btn-primary">
              Copy Table to Clipboard
            </TableCopyButton>
          )}
        </div>

        {rows.length === 0 ? (
          <ExcelCopy />
        ) : (
          <>
            <TableImportUi />
            <ParsedRows id="parsed-rows" />
          </>
        )}
      </div>
    </>
  )
}

function guesCountryColumn(header: string[]) {
  const candidate = _(header).find(h => h.toLowerCase().includes('country'))
  return candidate || header[0]
}

function guessNumberColumn(header: string[]) {
  const candidate = _(header).find(h => {
    const hlc = h.toLowerCase()
    return hlc.includes('patent') || hlc.includes('grant') || hlc.includes('publication')
  })
  return candidate || header[1]
}

function guessFamilyColumn(header: string[]) {
  const candidate = _(header).find(h => h.toLowerCase().includes('famil'))
  return candidate || header[2]
}

const NO_COLUMN = '-'

function guessIpTypeColumn(header: string[]) {
  const candidate = _(header).find(h => h.replaceAll(' ', '').toLowerCase().includes('iptype'))
  return candidate || NO_COLUMN
}

function guessValidatedColumn(header: string[]) {
  const candidate = _(header).find(h => h.replaceAll(' ', '').toLowerCase().includes('valida'))
  return candidate || NO_COLUMN
}

function asBoolean(value: string | undefined): boolean | undefined {
  if (!value) return undefined;
  const lc = value.toLowerCase();
  if (lc === 'true' || lc === '1') return true;
  if (lc === 'false' || lc === '0') return false;
  return undefined;
}

function TableImportUi() {
  const {header, rows, setRawText, setStandardizedNumbers, setEpoData} = useContext(ExcelCopyContext);

  const [countryColumn, setCountryColumn] = useState(guesCountryColumn(header))
  const [numberColumn, setNumberColumn] = useState(guessNumberColumn(header))
  const [familyColumn, setFamilyColumn] = useState(guessFamilyColumn(header))
  const [ipTypeColumn, setIpTypeColumn] = useState(guessIpTypeColumn(header))
  const [validatedColumn, setValidatedColumn] = useState(guessValidatedColumn(header))
  // console.log({countryColumn, numberColumn})

  async function importRows() {
    const publicationNumbers = {}
    const familyRefs = []
    const newStandardizedNumbers = rows.map(row => {
      const countryCode = standardizeCountryCode(row[countryColumn]);
      const number = row[numberColumn];
      const family = row[familyColumn];
      if (family && !familyRefs.includes(family)) {
        familyRefs.push(family);
      }
      const ipType = ipTypeColumn !== NO_COLUMN ? deduceIpType(row[ipTypeColumn]) : undefined;
      const validated = validatedColumn !== NO_COLUMN ? asBoolean(row[validatedColumn]) : undefined;
      const docNumber = createStandardizedPatentNumber({countryCode, ipType}, number);
      const m: EpoScrapeMember = {docNumber, countryCode, validated};
      publicationNumbers[family] = [...(publicationNumbers[family] || []), m];
      return docNumber
    });
    setStandardizedNumbers(newStandardizedNumbers);

    const epoDataRaw = await epoLoad({ publicationNumbers, type: 'bulk' });
    const epoData = familyRefs.flatMap(family => epoDataRaw[family] || []);
    setEpoData(epoData);
    console.log({epoData});
  }

  const handlingColumns = [
    {label: 'Country Column', value: countryColumn, setter: setCountryColumn},
    {label: 'Publication Number Column', value: numberColumn, setter: setNumberColumn},
    {label: 'Family ID Column', value: familyColumn, setter: setFamilyColumn},
    {label: 'IP Type Column (optional)', value: ipTypeColumn, setter: setIpTypeColumn, optional: true},
    {label: 'validated (optional)', value: validatedColumn, setter: setValidatedColumn, optional: true},
  ]

  return  (
    <div className="grid grid-cols-[auto_auto] max-w-max gap-4 py-4">
      {handlingColumns.map(({label, value, setter, optional}) =>
      <label key={label} className="col-span-2 grid grid-cols-subgrid items-baseline">
        <span className="label">{label}</span>
        <select className="form-select" value={value} onChange={e => setter(e.target.value)}>
          {optional && <option key={NO_COLUMN} value={NO_COLUMN}>-- None --</option>}
          {header.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
      </label>
      )}

      <div className="col-span-2 pt-4 flex flex-row-reverse gap-4">
        <button onClick={importRows} className="btn-primary">Import</button>
        <button onClick={() => setRawText('')} className="btn-secondary">Cancel</button>
      </div>
    </div>
  )
}